'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ConnectorLines from './ConnectorLines';
import PedigreeCard from './PedigreeCard';
import {
  CELL_PADDING,
  COL_GAP,
  COL_WIDTH,
  DEFAULT_PEDIGREE_DEPTH,
  MAX_PEDIGREE_DEPTH,
  MAX_ROW_HEIGHT,
  MIN_ROW_HEIGHT,
  SCROLL_DIRECTION,
  type ScrollDirection,
} from './constants';
import { buildGridCells, getGenerationLabel, getTreeDepth } from './helpers';
import type { PedigreeEntry } from './types';

export type { PedigreeEntry } from './types';

/**
 * Horizontally-scrollable pedigree chart (CSS grid + SVG connector overlay).
 * Default depth is clamped with a "Show more" toggle; mobile gets a "Show
 * details" toggle and narrower cards.
 */
export default function PedigreeTree({
  root,
  linkRootAnimal,
  nodeHrefPrefix,
  interactive,
  selectedPath,
  onSelectPath,
  assignablePaths,
  placeholderPaths,
  defaultVisibleDepth,
  minVisibleDepth,
}: {
  root: PedigreeEntry;
  linkRootAnimal?: boolean;
  /** Base path for animal links. Empty disables linking. Final href = `${prefix}/${node.id}`. */
  nodeHrefPrefix: string;
  interactive?: boolean;
  selectedPath?: string | null;
  onSelectPath?: (path: string) => void;
  /** Paths that can be selected and assigned in interactive mode. */
  assignablePaths?: ReadonlySet<string>;
  /** Empty slots to render (e.g. sire/dam) even when not yet assignable. */
  placeholderPaths?: ReadonlySet<string>;
  /** When set, initial visible depth (e.g. 5 for registration). */
  defaultVisibleDepth?: number;
  /**
   * Forces the tree to render at least this many generations even when the
   * loaded data is shallower — used in registration to reveal the empty
   * ancestor slots one generation past the deepest assigned animal.
   */
  minVisibleDepth?: number;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Direction of last scroll — drives which side cards slide in from.
  const scrollDirectionRef = useRef<ScrollDirection>(SCROLL_DIRECTION.RIGHT);
  const prevScrollLeftRef = useRef<number>(0);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colWidth = isMobile ? 110 : COL_WIDTH;

  const [showAll, setShowAll] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  // Floor at 2 so a single-animal tree still shows a sire/dam column.
  const dataDepth = useMemo(() => Math.max(2, getTreeDepth(root)), [root]);
  // Rendering every empty ancestor slot grows exponentially, so keep the
  // browser-facing depth capped. `minVisibleDepth` raises the floor so empty
  // assignable slots one generation past the data are still rendered.
  const desiredDepth = Math.max(dataDepth, minVisibleDepth ?? 0);
  const visibleDataDepth = Math.min(desiredDepth, MAX_PEDIGREE_DEPTH);
  const initialDepth = Math.max(
    defaultVisibleDepth ?? DEFAULT_PEDIGREE_DEPTH,
    minVisibleDepth ?? 0,
  );
  const maxDepth = showAll
    ? visibleDataDepth
    : Math.min(initialDepth, visibleDataDepth);
  const hasMore = visibleDataDepth > initialDepth;

  const cells = useMemo(() => buildGridCells(root, maxDepth), [root, maxDepth]);
  const totalRows = Math.pow(2, maxDepth - 1);
  const minRowHeight = showDetails && isMobile ? 132 : MIN_ROW_HEIGHT;
  const maxRowHeight = showDetails && isMobile ? 160 : MAX_ROW_HEIGHT;
  // 280 = target overall tree height; clamp keeps cards legible at any depth.
  const rowHeightPx = Math.min(
    maxRowHeight,
    Math.max(minRowHeight, Math.floor(280 / totalRows)),
  );
  const treeHeight = totalRows * rowHeightPx;

  const updateScrollState = useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    const newScrollLeft = scrollElement.scrollLeft;
    if (newScrollLeft !== prevScrollLeftRef.current) {
      scrollDirectionRef.current =
        newScrollLeft > prevScrollLeftRef.current
          ? SCROLL_DIRECTION.RIGHT
          : SCROLL_DIRECTION.LEFT;
      prevScrollLeftRef.current = newScrollLeft;
    }
    setCanScrollLeft(newScrollLeft > 0);
    // `-1` guards fractional scrollWidths that would leave the right arrow stuck on.
    setCanScrollRight(
      newScrollLeft + scrollElement.clientWidth < scrollElement.scrollWidth - 1,
    );
  }, []);

  // Re-runs on `maxDepth` because the toggle rebuilds tree width.
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    updateScrollState();
    scrollElement.addEventListener('scroll', updateScrollState, {
      passive: true,
    });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(scrollElement);
    return () => {
      scrollElement.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, maxDepth]);

  // Per-cell entrance animation as cells scroll into view. Direct style mutation
  // (not React state) since the transitions are independent and purely visual.
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            element.style.opacity = '1';
            element.style.transform = 'translateX(0)';
          } else {
            const offset =
              scrollDirectionRef.current === SCROLL_DIRECTION.RIGHT
                ? '-12px'
                : '12px';
            element.style.opacity = '0';
            element.style.transform = `translateX(${offset})`;
          }
        });
      },
      { root: scrollElement, threshold: 0.05 },
    );

    scrollElement
      .querySelectorAll('[data-pedigree-cell]')
      .forEach((cellElement) => observer.observe(cellElement));
    return () => observer.disconnect();
  }, [cells]);

  const scrollLeft = () =>
    scrollRef.current?.scrollBy({
      left: -(colWidth + COL_GAP),
      behavior: 'smooth',
    });
  const scrollRight = () =>
    scrollRef.current?.scrollBy({
      left: colWidth + COL_GAP,
      behavior: 'smooth',
    });

  return (
    <Box sx={{ position: 'relative' }}>
      {canScrollLeft && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={scrollLeft}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 3,
              '&:hover': { backgroundColor: 'action.hover' },
              width: 40,
              height: 40,
            }}
            aria-label="Scroll left"
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      )}

      {canScrollRight && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={scrollRight}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 3,
              '&:hover': { backgroundColor: 'action.hover' },
              width: 40,
              height: 40,
            }}
            aria-label="Scroll right"
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      <Box
        ref={scrollRef}
        role="region"
        aria-label="Pedigree chart"
        sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 1 }}
      >
        <Box sx={{ minWidth: 'max-content' }}>
          {/* Generation header labels — scroll with the tree */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${maxDepth}, ${colWidth}px)`,
              columnGap: `${COL_GAP}px`,
              mb: 0.75,
            }}
          >
            {Array.from({ length: maxDepth }, (_unused, columnIndex) => (
              <Typography
                key={columnIndex}
                sx={{
                  textAlign: 'center',
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                }}
              >
                {getGenerationLabel(columnIndex)}
              </Typography>
            ))}
          </Box>

          {/* CSS grid tree with SVG connector overlay */}
          <Box
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: `repeat(${maxDepth}, ${colWidth}px)`,
              gridTemplateRows: `repeat(${totalRows}, ${rowHeightPx}px)`,
              columnGap: `${COL_GAP}px`,
              height: `${treeHeight}px`,
              transition: 'grid-template-rows 0.3s ease, height 0.3s ease',
            }}
          >
            <ConnectorLines
              cells={cells}
              colWidth={colWidth}
              maxDepth={maxDepth}
              rowHeightPx={rowHeightPx}
              treeHeight={treeHeight}
            />

            {cells
              .filter(
                (cell) =>
                  cell.node !== null ||
                  (interactive &&
                    !cell.node &&
                    (assignablePaths?.has(cell.path) ||
                      placeholderPaths?.has(cell.path))),
              )
              .map((cell) => {
                const isAssignable =
                  !!assignablePaths?.has(cell.path) && !cell.node;
                const isSelectable =
                  !!interactive &&
                  !!onSelectPath &&
                  (cell.node != null || isAssignable);
                // Top-down stagger; capped so deep trees don't take forever.
                const staggerMs = Math.min((cell.row - 1) * 20, 150);
                return (
                  <Box
                    key={`${cell.col}-${cell.row}`}
                    data-pedigree-cell
                    sx={{
                      gridColumn: cell.col,
                      gridRow: `${cell.row} / span ${cell.rowSpan}`,
                      p: `${CELL_PADDING}px`,
                    }}
                    // Matches the off-screen branch above so the first
                    // IntersectionObserver fire triggers the entrance.
                    style={{
                      opacity: 0,
                      transform: 'translateX(12px)',
                      transition: `opacity 0.25s ease ${staggerMs}ms, transform 0.25s ease ${staggerMs}ms`,
                    }}
                  >
                    <PedigreeCard
                      cell={cell}
                      nodeHrefPrefix={nodeHrefPrefix}
                      linkRootAnimal={linkRootAnimal}
                      isMobile={isMobile}
                      showDetails={showDetails}
                      interactive={interactive}
                      selected={selectedPath === cell.path}
                      assignable={isAssignable}
                      onSelect={
                        isSelectable
                          ? () => onSelectPath!(cell.path)
                          : undefined
                      }
                    />
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>

      {(hasMore || isMobile) && (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {hasMore && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? 'Show fewer generations' : 'Show more generations'}
            </Button>
          )}
          {isMobile && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowDetails((value) => !value)}
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
